/**
 * Workout Page — Orchestrates the full active session lifecycle
 *
 * States:
 *   idle       → auto-generates on mount; shows spinner
 *   generating → loading spinner
 *   preview    → workout generated, shown for review; timer not yet running
 *   active     → user tapped "Start Workout"; timer running, sets being logged
 *   complete   → session saved, split day advanced
 *   abandoned  → session discarded (auto-returns to idle)
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import { repository } from '@/lib/DexieRepository';
import type { GeneratedExercise, SplitDay, SplitType, Exercise, MuscleGroup } from '@/types';
import { useWorkoutSession, formatElapsed } from '@/hooks/useWorkoutSession';
import ExerciseCard from '@/components/workout/ExerciseCard';
import PRCelebration from '@/components/workout/PRCelebration';
import WorkoutSummary from '@/components/workout/WorkoutSummary';
import SupersetCard from '@/components/workout/SupersetCard';

// ── Target muscle % helpers ───────────────────────────────────

const MUSCLE_DISPLAY: Partial<Record<MuscleGroup, string>> = {
  chest:        'Chest',
  lats:         'Back',
  quads:        'Quads',
  hamstrings:   'Hamstrings',
  glutes:       'Glutes',
  front_delt:   'Front Delts',
  lateral_delt: 'Side Delts',
  rear_delt:    'Rear Delts',
  biceps:       'Biceps',
  triceps:      'Triceps',
  traps:        'Traps',
  calves:       'Calves',
  core:         'Core',
};

/**
 * For a list of generated exercises, returns the top muscles by weighted
 * set-volume contribution, as { muscle, pct } sorted descending.
 * Only returns muscles with ≥ 5% share, capped at 4.
 */
function computeTargetMuscles(
  exercises: GeneratedExercise[],
): Array<{ muscle: MuscleGroup; pct: number }> {
  const vol = new Map<MuscleGroup, number>();
  let total = 0;

  for (const { exercise, workingSets } of exercises) {
    const sets = workingSets.length || 1;
    for (const alloc of exercise.muscles) {
      const contribution = sets * alloc.fraction;
      vol.set(alloc.muscle, (vol.get(alloc.muscle) ?? 0) + contribution);
      total += contribution;
    }
  }

  if (total === 0) return [];

  return [...vol.entries()]
    .map(([muscle, v]) => ({ muscle, pct: Math.round((v / total) * 100) }))
    .filter(({ pct }) => pct >= 5)
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 4);
}

/** Pill row showing top target muscles with %. */
function TargetMusclesRow({ exercises }: { exercises: GeneratedExercise[] }) {
  const muscles = useMemo(() => computeTargetMuscles(exercises), [exercises]);
  if (muscles.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {muscles.map(({ muscle, pct }) => (
        <div
          key={muscle}
          className="flex items-center gap-1.5 bg-surface-300 rounded-full px-3 py-1"
        >
          <span className="text-xs font-medium text-ink">
            {MUSCLE_DISPLAY[muscle] ?? muscle.replace(/_/g, ' ')}
          </span>
          <span className="text-xs font-bold text-accent">{pct}%</span>
        </div>
      ))}
    </div>
  );
}

/** Returns the index of the first compound exercise, or -1 if none. */
function findFocusExerciseIndex(exercises: GeneratedExercise[]): number {
  return exercises.findIndex((e) => e.exercise.category === 'compound');
}

// ── Split day / type helpers ─────────────────────────────────

const SPLIT_DAY_INFO: Record<SplitDay, { label: string; subtitle: string; emoji: string }> = {
  push:          { label: 'Push Day',   emoji: '🔺', subtitle: 'Chest · Shoulders · Triceps · Quads' },
  anterior:      { label: 'Push Day',   emoji: '🔺', subtitle: 'Chest · Shoulders · Triceps · Quads' },
  pull:          { label: 'Pull Day',   emoji: '🔻', subtitle: 'Back · Rear Delts · Biceps · Hamstrings' },
  posterior:     { label: 'Pull Day',   emoji: '🔻', subtitle: 'Back · Rear Delts · Biceps · Hamstrings' },
  legs:          { label: 'Leg Day',    emoji: '🦵', subtitle: 'Quads · Hamstrings · Glutes · Calves' },
  upper:         { label: 'Upper Body', emoji: '💪', subtitle: 'Chest · Back · Shoulders · Arms' },
  lower:         { label: 'Lower Body', emoji: '🦿', subtitle: 'Quads · Hamstrings · Glutes · Core' },
  full_body_day: { label: 'Full Body',  emoji: '⚡', subtitle: 'Chest · Back · Legs · Shoulders · Arms' },
};

const SPLIT_TYPE_INFO: Record<SplitType, { label: string; days: string }> = {
  push_pull:      { label: 'Push / Pull',       days: 'Push → Pull' },
  push_pull_legs: { label: 'Push / Pull / Legs', days: 'Push → Pull → Legs' },
  upper_lower:    { label: 'Upper / Lower',      days: 'Upper → Lower' },
  full_body:      { label: 'Full Body',          days: 'Every session' },
};

const SPLIT_TYPE_FIRST_DAY: Record<SplitType, SplitDay> = {
  push_pull:      'push',
  push_pull_legs: 'push',
  upper_lower:    'upper',
  full_body:      'full_body_day',
};

function splitDayLabel(day: string): string {
  const MAP: Record<string, string> = {
    push: 'Push Day', anterior: 'Push Day',
    pull: 'Pull Day', posterior: 'Pull Day',
    legs: 'Leg Day',
    upper: 'Upper Body',
    lower: 'Lower Body',
    full_body_day: 'Full Body',
  };
  return MAP[day] ?? day;
}

// ── Split Picker Modal ───────────────────────────────────────

function SplitPickerModal({ current, onClose }: { current: SplitType; onClose: () => void }) {
  const splits: SplitType[] = ['push_pull', 'push_pull_legs', 'upper_lower', 'full_body'];

  const handleSelect = async (type: SplitType) => {
    await repository.updateSettings({
      splitType: type,
      nextSplitDay: SPLIT_TYPE_FIRST_DAY[type],
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pb-safe">
      <button className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-surface-100 rounded-t-3xl
                      pt-4 pb-8 px-5 shadow-card-lg animate-slide-up">
        <div className="w-10 h-1 bg-surface-400 rounded-full mx-auto mb-5" />
        <h3 className="text-lg font-bold text-ink mb-1">Training Split</h3>
        <p className="text-xs text-ink-muted mb-4">Changing your split resets to the first day of the new cycle.</p>
        <div className="space-y-2">
          {splits.map((type) => {
            const info = SPLIT_TYPE_INFO[type];
            const active = type === current;
            return (
              <button
                key={type}
                onClick={() => handleSelect(type)}
                className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl text-left
                            border transition-all active:scale-[0.98]
                            ${active
                              ? 'bg-accent/10 border-accent/40'
                              : 'bg-surface-200 border-surface-400 hover:bg-surface-300'}`}
              >
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold text-sm ${active ? 'text-accent' : 'text-ink'}`}>
                    {info.label}
                  </p>
                  <p className="text-xs text-ink-muted mt-0.5">{info.days}</p>
                </div>
                {active && <span className="text-accent text-lg flex-shrink-0">✓</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Template picker (shown as bottom sheet when there are templates) ─

function TemplatePickerButton({ onSelect }: { onSelect: (splitDay: SplitDay) => void }) {
  const [open, setOpen] = useState(false);
  const templates = useLiveQuery(() =>
    db.workoutTemplates.orderBy('createdAt').reverse().toArray(), []
  ) ?? [];

  const SPLIT_LABEL: Record<string, string> = {
    push: 'Push', anterior: 'Push', pull: 'Pull', posterior: 'Pull',
    legs: 'Legs', upper: 'Upper', lower: 'Lower', full_body_day: 'Full Body',
  };

  if (templates.length === 0) return null;

  return (
    <>
      <button className="btn-ghost text-sm text-ink-muted" onClick={() => setOpen(true)}>
        Use a Template
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center pb-safe">
          <button className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-lg bg-surface-100 rounded-t-3xl
                          pt-4 pb-6 px-6 shadow-card-lg animate-slide-up">
            <div className="w-10 h-1 bg-surface-400 rounded-full mx-auto mb-5" />
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-ink">Templates</h3>
              <button className="btn-ghost p-1 text-lg" onClick={() => setOpen(false)}>✕</button>
            </div>
            <div className="space-y-2">
              {templates.map((t) => (
                <button key={t.id}
                  className="w-full card flex items-center gap-3 text-left
                             hover:bg-surface-200 active:scale-[0.98] transition-all"
                  onClick={async () => {
                    await db.workoutTemplates.update(t.id, { lastUsedAt: new Date().toISOString() });
                    setOpen(false);
                    onSelect(t.splitDay as SplitDay);
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink text-sm">{t.name}</p>
                    <p className="text-xs text-ink-muted mt-0.5">
                      {SPLIT_LABEL[t.splitDay] ?? t.splitDay} · {t.exerciseIds.length} exercises
                    </p>
                  </div>
                  <span className="text-ink-faint">›</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Generating State ─────────────────────────────────────────

function GeneratingState({ splitDay, splitType, onChangeSplit }: {
  splitDay: string;
  splitType: SplitType;
  onChangeSplit: () => void;
}) {
  const dayInfo = SPLIT_DAY_INFO[splitDay as SplitDay] ?? SPLIT_DAY_INFO['push'];
  const splitInfo = SPLIT_TYPE_INFO[splitType];

  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] gap-5 animate-fade-in">
      <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center
                      animate-pulse-slow">
        <svg viewBox="0 0 24 24" className="w-8 h-8 text-accent" fill="none"
          stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
        </svg>
      </div>
      <div className="text-center">
        <h2 className="text-2xl font-bold text-ink tracking-tight">
          {dayInfo.emoji} {dayInfo.label}
        </h2>
        <p className="text-ink-muted text-sm mt-1">{dayInfo.subtitle}</p>
      </div>
      <button
        onClick={onChangeSplit}
        className="flex items-center gap-2 px-4 py-2 rounded-full bg-surface-300
                   border border-surface-400 hover:bg-surface-400 transition-colors active:scale-95"
      >
        <span className="text-xs text-ink-muted">{splitInfo.label}</span>
        <span className="text-xs text-ink-faint">· change ›</span>
      </button>
      <p className="text-sm text-ink-muted animate-pulse">Calculating fatigue & overload targets…</p>
    </div>
  );
}

// ── Error / Retry State ──────────────────────────────────────

function ErrorState({ error, onRetry, onChangeSplit, splitDay, onSelectTemplate }: {
  error: string;
  onRetry: () => void;
  onChangeSplit: () => void;
  splitDay: string;
  onSelectTemplate: (day: SplitDay) => void;
}) {
  const dayInfo = SPLIT_DAY_INFO[splitDay as SplitDay] ?? SPLIT_DAY_INFO['push'];

  return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] px-6 text-center space-y-5 animate-fade-in">
      <div className="w-16 h-16 rounded-2xl bg-danger/10 flex items-center justify-center">
        <span className="text-2xl">⚠</span>
      </div>
      <div>
        <h2 className="text-xl font-bold text-ink">Couldn't generate workout</h2>
        <p className="text-sm text-ink-muted mt-1">{error}</p>
      </div>
      <button
        onClick={onChangeSplit}
        className="flex items-center gap-2 px-4 py-2 rounded-full bg-surface-300
                   border border-surface-400 hover:bg-surface-400 transition-colors"
      >
        <span className="text-sm text-ink-muted">{dayInfo.emoji} {dayInfo.label}</span>
        <span className="text-xs text-ink-faint">· change ›</span>
      </button>
      <button className="btn-primary w-full max-w-xs py-4" onClick={onRetry}>
        Try Again
      </button>
      <TemplatePickerButton onSelect={onSelectTemplate} />
    </div>
  );
}

// ── Session Header (pinned) ───────────────────────────────────

function SessionHeader({
  elapsed, splitDay, totalSets, loggedCount, note, onNoteChange, onAbandon, onComplete,
}: {
  elapsed: number;
  splitDay: string;
  totalSets: number;
  loggedCount: number;
  note: string;
  onNoteChange: (n: string) => void;
  onAbandon: () => void;
  onComplete: () => void;
}) {
  const [confirmAbandon, setConfirmAbandon] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const progressPct = totalSets > 0 ? (loggedCount / totalSets) * 100 : 0;
  const canFinish = loggedCount >= Math.ceil(totalSets * 0.5);

  return (
    <div className="bg-surface-100/95 backdrop-blur-xl border-b border-surface-400
                    px-4 py-3 sticky top-0 z-10">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-xs text-ink-faint">{splitDayLabel(splitDay)}</p>
          <p className="text-xl font-bold font-mono text-ink">{formatElapsed(elapsed)}</p>
        </div>

        <div className="flex items-center gap-2">
          {canFinish && (
            <button className="btn-primary text-sm px-4 py-2" onClick={onComplete}>
              Finish
            </button>
          )}
          <button
            className="w-9 h-9 rounded-xl bg-surface-300 flex items-center justify-center
                       text-ink-muted text-sm hover:bg-surface-400 transition-colors"
            onClick={() => setConfirmAbandon(true)}
            title="Abandon workout"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Session note */}
      {noteOpen ? (
        <div className="flex gap-2 mb-2">
          <input
            type="text" value={note}
            onChange={(e) => onNoteChange(e.target.value)}
            placeholder="Session note…"
            autoFocus
            className="flex-1 bg-surface-300 rounded-lg px-3 py-2 text-xs text-ink
                       focus:outline-none focus:ring-1 focus:ring-accent"
          />
          <button className="text-xs text-accent font-medium px-2"
            onClick={() => setNoteOpen(false)}>Done</button>
        </div>
      ) : (
        <button className="text-xs text-ink-faint mb-1.5 text-left w-full
                           hover:text-ink-muted transition-colors"
          onClick={() => setNoteOpen(true)}>
          {note ? `📝 ${note}` : '+ Session note'}
        </button>
      )}

      {/* Progress bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-surface-400 rounded-full overflow-hidden">
          <div
            className="h-full bg-accent rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className="text-xs text-ink-faint font-mono">{loggedCount}/{totalSets}</span>
      </div>

      {confirmAbandon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/60">
          <div className="card-elevated w-full max-w-xs text-center space-y-4 p-6 animate-fade-in">
            <p className="font-bold text-ink text-lg">Abandon Workout?</p>
            <p className="text-ink-muted text-sm">
              Progress will be saved, but the session will be marked abandoned.
            </p>
            <div className="flex gap-3">
              <button className="btn-secondary flex-1 text-sm" onClick={() => setConfirmAbandon(false)}>
                Keep Going
              </button>
              <button className="btn-danger flex-1 text-sm" onClick={onAbandon}>
                Abandon
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Add Exercise Modal ────────────────────────────────────────

function AddExerciseModal({
  currentExerciseIds,
  onAdd,
  onClose,
}: {
  currentExerciseIds: Set<string>;
  onAdd: (ex: Exercise) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const allExercises = useLiveQuery(() => db.exercises.orderBy('name').toArray(), []) ?? [];

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return allExercises.filter(
      (e) => !currentExerciseIds.has(e.id) &&
             (q === '' || e.name.toLowerCase().includes(q))
    );
  }, [allExercises, query, currentExerciseIds]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pb-safe">
      <button className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-surface-100 rounded-t-3xl
                      pt-4 pb-6 shadow-card-lg animate-slide-up flex flex-col"
           style={{ maxHeight: '80vh' }}>
        <div className="w-10 h-1 bg-surface-400 rounded-full mx-auto mb-4" />
        <div className="px-5 mb-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-ink">Add Exercise</h3>
            <button className="btn-ghost p-1 text-lg" onClick={onClose}>✕</button>
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search exercises…"
            autoFocus
            className="w-full bg-surface-300 rounded-xl px-3 py-3 text-sm text-ink
                       focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
        <div className="overflow-y-auto px-5 space-y-1.5">
          {filtered.length === 0 && (
            <p className="text-sm text-ink-faint text-center py-8">No exercises found</p>
          )}
          {filtered.map((ex) => (
            <button
              key={ex.id}
              onClick={() => { onAdd(ex); onClose(); }}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl
                         bg-surface-200 hover:bg-surface-300 active:scale-[0.98]
                         transition-all text-left"
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-ink truncate">{ex.name}</p>
                <p className="text-xs text-ink-muted mt-0.5">
                  {ex.muscles[0]?.muscle.replace(/_/g, ' ')} · {ex.category}
                </p>
              </div>
              <span className={`badge text-xs flex-shrink-0
                ${ex.category === 'compound' ? 'badge-accent' : 'bg-surface-400 text-ink-muted'}`}>
                {ex.category}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────

export default function WorkoutPage() {
  const session = useWorkoutSession();
  const location = useLocation();
  const routeState = location.state as { splitDayOverride?: SplitDay; durationOverride?: number } | null;
  const splitDayOverride = routeState?.splitDayOverride;
  const durationOverride = routeState?.durationOverride;
  const settings = useLiveQuery(() => db.settings.get('singleton'), []);
  const gymProfile = useLiveQuery(async () => {
    const all = await db.gymProfiles.toArray();
    return all.find((p) => p.isActive) ?? all[0];
  }, []);

  const availableEquipment = gymProfile?.availableEquipment ?? [];
  const audioEnabled = settings?.audioEnabled ?? true;
  const [sessionNote, setSessionNote] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [showSplitPicker, setShowSplitPicker] = useState(false);
  const activeCardRef = useRef<HTMLDivElement>(null);

  const [supersetPairs, setSupersetPairs] = useState<Map<number, number>>(new Map());
  const [showAddExercise, setShowAddExercise] = useState(false);

  const pairAsSuperset = useCallback((indexA: number, indexB: number) => {
    setSupersetPairs((prev) => {
      const next = new Map(prev);
      next.set(indexA, indexB);
      next.set(indexB, indexA);
      return next;
    });
  }, []);

  const unpairSuperset = useCallback((indexA: number) => {
    setSupersetPairs((prev) => {
      const next = new Map(prev);
      const indexB = next.get(indexA);
      next.delete(indexA);
      if (indexB !== undefined) next.delete(indexB);
      return next;
    });
  }, []);

  // On mount: try to restore an in-progress session first.
  // If nothing to restore, auto-generate so the user sees a preview.
  useEffect(() => {
    if (session.phase === 'idle' && settings) {
      session.tryRestore().then((restored) => {
        if (!restored) session.generate(splitDayOverride, durationOverride);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.phase, !!settings]);

  const totalLoggedSets = [...session.loggedSets.values()]
    .flat()
    .filter((s) => !s.isWarmup).length;
  const totalWorkingSets = session.plan?.totalWorkingSets ?? 0;
  const exerciseCount = session.plan?.exercises.length ?? 0;

  // Auto-advance when current exercise fully logged
  useEffect(() => {
    const p = session.plan;
    if (!p) return;
    const genEx = p.exercises[activeIndex];
    if (!genEx) return;
    const logged = session.loggedSets.get(genEx.exercise.id) ?? [];
    const workingLogged = logged.filter((s) => !s.isWarmup).length;
    const workingTotal  = genEx.workingSets.length;
    if (workingLogged >= workingTotal && workingTotal > 0) {
      const nextIdx = activeIndex + 1;
      if (nextIdx < p.exercises.length) {
        setActiveIndex(nextIdx);
        setTimeout(() => activeCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.loggedSets]);

  const usageCount = new Map<string, number>();
  for (const sets of session.loggedSets.values()) {
    for (const s of sets) {
      usageCount.set(s.exerciseId, (usageCount.get(s.exerciseId) ?? 0) + 1);
    }
  }

  const nextDay   = (settings?.nextSplitDay ?? 'push') as SplitDay;
  const splitType = (settings?.splitType ?? 'push_pull') as SplitType;

  // ── Render by phase ──────────────────────────────────────────

  // Generating: show spinner
  if (session.phase === 'idle' || session.phase === 'generating') {
    // If there's an error from a failed generate, show error/retry state
    if (session.error) {
      return (
        <div className="content-pad-bottom">
          <ErrorState
            error={session.error}
            onRetry={() => session.generate()}
            onChangeSplit={() => setShowSplitPicker(true)}
            splitDay={nextDay}
            onSelectTemplate={(day) => session.generate(day)}
          />
          {showSplitPicker && (
            <SplitPickerModal
              current={splitType}
              onClose={() => setShowSplitPicker(false)}
            />
          )}
        </div>
      );
    }

    return (
      <div className="content-pad-bottom">
        <GeneratingState
          splitDay={nextDay}
          splitType={splitType}
          onChangeSplit={() => setShowSplitPicker(true)}
        />
        {showSplitPicker && (
          <SplitPickerModal
            current={splitType}
            onClose={() => {
              setShowSplitPicker(false);
              // Re-generate with new split day after settings update
              setTimeout(() => session.generate(), 100);
            }}
          />
        )}
      </div>
    );
  }

  // Preview — workout generated, timer not yet running
  if (session.phase === 'preview' && session.plan) {
    const plan = session.plan;
    const dayInfo = SPLIT_DAY_INFO[plan.splitDay] ?? SPLIT_DAY_INFO['push'];
    return (
      <div className="content-pad-bottom animate-fade-in">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-surface/95 backdrop-blur-xl px-4 pt-6 pb-4
                        border-b border-surface-400">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-ink tracking-tight">{dayInfo.emoji} {dayInfo.label}</h1>
              <p className="text-xs text-ink-muted mt-0.5">
                {plan.exercises.length} exercises · {plan.totalWorkingSets} sets
                {plan.warnings.length > 0 && ` · ${plan.warnings.length} note${plan.warnings.length > 1 ? 's' : ''}`}
              </p>
              <TargetMusclesRow exercises={plan.exercises} />
            </div>
            <button
              className="btn-primary px-6 py-3 rounded-xl text-base font-semibold whitespace-nowrap self-start"
              onClick={() => session.beginWorkout()}
            >
              Start
            </button>
          </div>
        </div>

        {/* Exercise list preview */}
        <div className="px-4 pt-4 space-y-3 pb-8">
          {(() => {
            const focusIdx = findFocusExerciseIndex(plan.exercises);
            return plan.exercises.map((genEx, i) => {
              const isFocus = i === focusIdx;
              return (
                <div key={genEx.exercise.id} className={`card flex items-center gap-3 ${isFocus ? 'ring-1 ring-accent/30' : ''}`}>
                  <span className="text-xs text-ink-faint w-5 text-right flex-shrink-0">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    {isFocus && (
                      <p className="text-xs font-bold text-accent uppercase tracking-widest mb-0.5">
                        Focus Exercise
                      </p>
                    )}
                    <p className="text-sm font-medium text-ink truncate">{genEx.exercise.name}</p>
                    <p className="text-xs text-ink-muted mt-0.5">
                      {genEx.workingSets.length} sets
                      {genEx.workingSets[0]?.targetReps
                        ? ` · ${genEx.workingSets[0].targetReps} reps`
                        : ''}
                    </p>
                  </div>
                  <span className="text-xs text-ink-faint capitalize flex-shrink-0">
                    {genEx.exercise.category}
                  </span>
                </div>
              );
            });
          })()}

          {plan.warnings.length > 0 && (
            <div className="card border border-warning/20 space-y-1 mt-2">
              {plan.warnings.map((w, i) => (
                <p key={i} className="text-xs text-warning">{w}</p>
              ))}
            </div>
          )}

          <button
            className="btn-primary w-full py-4 text-base font-semibold rounded-2xl mt-4"
            onClick={() => session.beginWorkout()}
          >
            Start Workout
          </button>
          <button
            className="w-full text-xs text-ink-faint py-2 hover:text-ink-muted transition-colors"
            onClick={() => session.generate(splitDayOverride, durationOverride)}
          >
            Regenerate
          </button>
        </div>
      </div>
    );
  }

  if (session.phase === 'abandoned') {
    // Reset triggers idle → auto-generate again
    return (
      <div className="content-pad-bottom">
        <GeneratingState
          splitDay={nextDay}
          splitType={splitType}
          onChangeSplit={() => setShowSplitPicker(true)}
        />
      </div>
    );
  }

  if (session.phase === 'complete' && session.plan) {
    return (
      <div className="overflow-y-auto no-scrollbar">
        <WorkoutSummary
          plan={session.plan}
          loggedSets={session.loggedSets}
          elapsedSeconds={session.elapsedSeconds}
          sessionPRs={session.sessionPRs}
          onDone={session.reset}
        />
      </div>
    );
  }

  // ── Active session ───────────────────────────────────────────

  const plan = session.plan!;

  return (
    <div className="content-pad-bottom">
      <SessionHeader
        elapsed={session.elapsedSeconds}
        splitDay={plan.splitDay}
        totalSets={totalWorkingSets}
        loggedCount={totalLoggedSets}
        note={sessionNote}
        onNoteChange={setSessionNote}
        onAbandon={session.abandonWorkout}
        onComplete={() => session.completeWorkout(sessionNote || undefined)}
      />

      {plan.warnings.length > 0 && (
        <div className="px-4 pt-2 space-y-1">
          {plan.warnings.map((w, i) => (
            <div key={i} className="bg-warning/10 rounded-xl px-3 py-2 text-warning text-xs">
              ⚠ {w}
            </div>
          ))}
        </div>
      )}

      {session.latestPR && (
        <div className="px-4 pt-2">
          <PRCelebration pr={session.latestPR} onDismiss={session.clearLatestPR} />
        </div>
      )}

      <div className="px-4 pt-3 pb-6 space-y-2">
        {/* Session meta row */}
        <div className="flex items-center gap-3 text-xs text-ink-muted pb-1">
          <span>~{Math.round(plan.estimatedDurationSeconds / 60)} min</span>
          <span className="text-surface-500">·</span>
          <span>{plan.totalWorkingSets} sets</span>
          <span className="text-surface-500">·</span>
          <span>{exerciseCount} exercises</span>
        </div>

        {(() => {
          const rendered = new Set<number>();
          const focusIdx = findFocusExerciseIndex(plan.exercises);
          return plan.exercises.map((genEx, i) => {
            if (rendered.has(i)) return null;
            const isActive  = i === activeIndex;
            const isFocus   = i === focusIdx;
            const partnerId = supersetPairs.get(i);

            if (partnerId !== undefined && plan.exercises[partnerId]) {
              rendered.add(i);
              rendered.add(partnerId);
              return (
                <div key={`ss-${i}-${partnerId}`} ref={isActive ? activeCardRef : null}>
                  <SupersetCard
                    exA={genEx}
                    exB={plan.exercises[partnerId]}
                    indexA={i}
                    indexB={partnerId}
                    loggedA={session.loggedSets.get(genEx.exercise.id) ?? []}
                    loggedB={session.loggedSets.get(plan.exercises[partnerId].exercise.id) ?? []}
                    audioEnabled={audioEnabled}
                    onLogSet={session.logSet}
                    onUnpair={() => unpairSuperset(i)}
                  />
                </div>
              );
            }

            rendered.add(i);

            return (
              <div key={`${genEx.exercise.id}-${i}`} ref={isActive ? activeCardRef : null}>
                {!isActive && (
                  <ExerciseCard
                    genExercise={genEx}
                    index={i}
                    loggedSets={session.loggedSets.get(genEx.exercise.id) ?? []}
                    availableEquipment={availableEquipment as string[]}
                    usageCount={usageCount}
                    audioEnabled={audioEnabled}
                    weightUnit={settings?.weightUnit ?? 'lbs'}
                    collapsed
                    onExpand={() => setActiveIndex(i)}
                    onLogSet={session.logSet}
                    onDeleteSet={session.deleteSet}
                    onSubstitute={session.substituteExercise}
                  />
                )}

                {isActive && (
                  <div className="rounded-2xl ring-2 ring-accent/30 overflow-hidden">
                    <button
                      className="w-full flex items-center gap-2 px-4 pt-3 pb-1 text-left"
                      onClick={() => setActiveIndex(-1)}
                    >
                      <span className="text-xs text-accent uppercase tracking-widest font-semibold">
                        {isFocus ? 'Focus Exercise' : 'Now logging'}
                      </span>
                      <span className="ml-auto text-ink-faint text-xs">tap to collapse ↑</span>
                    </button>
                    <ExerciseCard
                      genExercise={genEx}
                      index={i}
                      loggedSets={session.loggedSets.get(genEx.exercise.id) ?? []}
                      availableEquipment={availableEquipment as string[]}
                      usageCount={usageCount}
                      audioEnabled={audioEnabled}
                      weightUnit={settings?.weightUnit ?? 'lbs'}
                      onLogSet={session.logSet}
                      onDeleteSet={session.deleteSet}
                      onUpdateSet={session.updateSet}
                      onSubstitute={session.substituteExercise}
                    />
                  </div>
                )}

                {isActive && i < plan.exercises.length - 1 && !supersetPairs.has(i + 1) && (
                  <button
                    className="w-full text-xs text-ink-faint py-1.5 flex items-center
                               justify-center gap-1.5 hover:text-ink-muted transition-colors"
                    onClick={() => pairAsSuperset(i, i + 1)}
                  >
                    <span className="text-base leading-none">⇄</span> Pair with next as superset
                  </button>
                )}
              </div>
            );
          });
        })()}

        {/* Add exercise button */}
        <button
          className="w-full py-3 rounded-2xl border border-dashed border-surface-400
                     text-sm text-ink-faint hover:text-ink-muted hover:border-surface-500
                     transition-colors flex items-center justify-center gap-2"
          onClick={() => setShowAddExercise(true)}
        >
          <span className="text-base">+</span> Add Exercise
        </button>

        {totalLoggedSets >= Math.ceil(totalWorkingSets * 0.5) && (
          <button
            className="btn-primary w-full py-4 text-base mt-2"
            onClick={() => session.completeWorkout(sessionNote || undefined)}
          >
            Complete Workout 🏁
          </button>
        )}

        {showAddExercise && (
          <AddExerciseModal
            currentExerciseIds={new Set(plan.exercises.map((e) => e.exercise.id))}
            onAdd={(ex) => { session.addExercise(ex); setActiveIndex(plan.exercises.length); }}
            onClose={() => setShowAddExercise(false)}
          />
        )}
      </div>
    </div>
  );
}
